import React, { useEffect, useState } from "react";
import { apiGet } from "../../utils/api";
import { PageHeader, Spinner } from "../../components/ui";
import { FaSearch, FaFilter } from "react-icons/fa";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function ManageSubscriptions() {
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [page, setPage] = useState(0);
  const limit = 10;

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await apiGet("/api/admin/subscriptions/stats");
      setStats(res);
    } catch (err) {
      console.error("Failed to load subscription stats", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      fetchSubscriptions();
    }
  }, [loading, statusFilter, sourceFilter, searchTerm, page]);

  const fetchSubscriptions = async () => {
    try {
      const filters = new URLSearchParams();
      if (statusFilter !== "all") filters.append("status", statusFilter);
      if (sourceFilter !== "all") filters.append("source", sourceFilter);
      if (searchTerm) filters.append("search", searchTerm);
      filters.append("limit", limit);
      filters.append("offset", page * limit);

      const res = await apiGet(
        `/api/admin/subscriptions?${filters.toString()}`
      );
      setSubscriptions(res.subscriptions || []);
    } catch (err) {
      console.error("Failed to load subscriptions", err);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  const mrrChartData = stats?.mrrByMonth
    ? {
        labels: stats.mrrByMonth.map((m) => m.month),
        datasets: [
          {
            label: "Monthly Recurring Revenue",
            data: stats.mrrByMonth.map((m) => m.mrr),
            borderColor: "#3b82f6",
            backgroundColor: "rgba(59, 130, 246, 0.1)",
            tension: 0.4,
            fill: true,
          },
        ],
      }
    : null;

  const statusChartData = stats?.subscriptionsByStatus
    ? {
        labels: Object.keys(stats.subscriptionsByStatus),
        datasets: [
          {
            data: Object.values(stats.subscriptionsByStatus),
            backgroundColor: [
              "#10b981",
              "#ef4444",
              "#f59e0b",
              "#8b5cf6",
              "#6366f1",
            ],
          },
        ],
      }
    : null;

  const planChartData = stats?.subscriptionsByPlan
    ? {
        labels: Object.keys(stats.subscriptionsByPlan),
        datasets: [
          {
            label: "Subscriptions by Plan",
            data: Object.values(stats.subscriptionsByPlan),
            backgroundColor: [
              "#3b82f6",
              "#10b981",
              "#f59e0b",
              "#ef4444",
              "#8b5cf6",
            ],
          },
        ],
      }
    : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Subscription Management"
        subtitle="Track MRR, ARR, and customer subscriptions"
      />

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-600 font-medium">Total MRR</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatCurrency(stats?.summary?.totalMRR)}
          </p>
          <p className="text-xs text-gray-400 mt-1">Stripe-paid only</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-600 font-medium">Total ARR</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatCurrency(stats?.summary?.totalARR)}
          </p>
          <p className="text-xs text-gray-400 mt-1">Stripe-paid only</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-600 font-medium">Paid Active</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {stats?.summary?.paidActiveCount || 0}
          </p>
          <p className="text-xs text-gray-400 mt-1">via Stripe</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-600 font-medium">Admin Assigned</p>
          <p className="text-2xl font-bold text-blue-600 mt-1">
            {stats?.summary?.adminAssignedCount || 0}
          </p>
          <p className="text-xs text-gray-400 mt-1">manually assigned</p>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <p className="text-sm text-gray-600 font-medium">Refunds</p>
          <p className="text-2xl font-bold text-red-600 mt-1">
            {formatCurrency(stats?.summary?.totalRefunds)}
          </p>
          <p className="text-xs text-gray-400 mt-1">Walytic customers only</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {mrrChartData && (
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              MRR Trend (12 months)
            </h3>
            <Line data={mrrChartData} options={{ responsive: true }} />
          </div>
        )}

        {statusChartData && (
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Subscriptions by Status
            </h3>
            <Doughnut data={statusChartData} options={{ responsive: true }} />
          </div>
        )}

        {planChartData && (
          <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm col-span-1 lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Subscriptions by Plan
            </h3>
            <Bar data={planChartData} options={{ responsive: true }} />
          </div>
        )}
      </div>

      {/* Subscriptions List */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Subscriptions
          </h3>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search by email or name..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center gap-2">
              <FaFilter className="text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="canceled">Canceled</option>
                <option value="incomplete">Incomplete</option>
                <option value="past_due">Past Due</option>
              </select>
              <select
                value={sourceFilter}
                onChange={(e) => {
                  setSourceFilter(e.target.value);
                  setPage(0);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Sources</option>
                <option value="stripe">Stripe Paid</option>
                <option value="trial">Pro Trial</option>
                <option value="admin">Admin Assigned</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Billing Cycle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Next Billing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">
                  Source
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {subscriptions.length > 0 ? (
                subscriptions.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {sub.user?.name || "N/A"}
                        </p>
                        <p className="text-xs text-gray-500">
                          {sub.user?.email || "N/A"}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <span>{sub.planName || sub.planKey || "N/A"}</span>
                        {sub.isTrial && (
                          <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-amber-100 text-amber-800 uppercase tracking-wide">
                            Trial
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          sub.isTrial
                            ? "bg-amber-100 text-amber-800"
                            : sub.status === "active"
                            ? "bg-green-100 text-green-800"
                            : sub.status === "expired"
                            ? "bg-gray-100 text-gray-800"
                            : sub.status === "canceled"
                            ? "bg-red-100 text-red-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {sub.isTrial
                          ? `trial${typeof sub.trialDaysLeft === "number" ? ` · ${sub.trialDaysLeft}d left` : ""}`
                          : sub.status}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-900">
                      {sub.isTrial ? "—" : formatCurrency(sub.amount)}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-900">
                      {sub.interval}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-900">
                      {new Date(sub.currentPeriodEnd).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-3">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          sub.source === "stripe"
                            ? "bg-purple-100 text-purple-800"
                            : sub.source === "trial"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {sub.source === "stripe"
                          ? "Stripe"
                          : sub.source === "trial"
                          ? "Trial"
                          : "Admin"}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No subscriptions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {subscriptions.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <button
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page + 1}
            </span>
            <button
              disabled={subscriptions.length < limit}
              onClick={() => setPage(page + 1)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
